import { FeatureFlag } from "@nirvana/shared/constants";
import { ButtonItem, ButtonWithActionItem } from "../../../../productTrials/msp/mspLandingPage/types";
import { translateFn } from '../../../../utils/translator.helper';

export enum MspInProductTrialCannotBeStartedReason {
  TrialValid = 0,
  ParentDoesNotResellProduct = 1,
  CompanyDoesNotHaveEndpointSecurityAsProductType = 2,
  StatusIsNotAvailable = 3,
  TrialAlreadyInProgressForCurrentProduct = 4,
  TrialAlreadyInProgressForAnotherProduct = 5,
  InCooldown = 6,
  CompanyIsSuspended = 7,
  ParentCompanyDoesNotDelegateTrialFeature = 8,
  CompanyDoesNotBelongInAnEligibleTreeHierarchy = 9,
  UserHasNoManageInventoryRight = 10,
  ParentCompanyHasNoContact = 11
}

export enum StartActionResponse {
  WithSuccessfulReconfigureTaskInitiation = 0,
  WithFailedReconfigureTaskInitiation = 1,
}

export enum StopActionResponse {
  WithSuccessfulReconfigureTaskInitiation = 0,
  WithFailedReconfigureTaskInitiation = 1,
  WithoutReconfigureTask = 2,
}

export enum ReconfigureEndpointsResponse {
  SuccessfulReconfigureInitiation = 0,
  SkippedReconfigureInitiation = 1,
}

export enum MspInProductTrialId {
  EdrTrial = 1,
  MdrTrial = 2,
}

export interface MspInProductTrialState {
  visible: boolean;
  trialState: MspInProductTrialCannotBeStartedReason;
  endDate: number | null;
  featuresInTrial: FeatureFlag[];
}

export enum MspInProductTrialEvents {
  TrialStatusModified = 'msp-in-product-trial-status-modified',
}

export const MODIFY_MSP_IPT_FLAGS_ERROR_DISTRIBUTOR_DOES_NOT_RESELL_PRODUCT = 1;

export const GzLandingPageProductLinks: { [productId in MspInProductTrialId]: { link: string; id: string } } = {
  [MspInProductTrialId.EdrTrial]: {
    link: 'https://www.bitdefender.com/business/support/en/77211-425479-endpoint-detection-and-response.html',
    id: 'msp-edr-ipt-landing-page',
  },
  [MspInProductTrialId.MdrTrial]: {
    link: 'https://www.bitdefender.com/business/support/en/77211-425480-managed-detection-and-response.html',
    id: 'msp-mdr-ipt-landing-page',
  },
};

export const GzLandingPageDemoLinks: { [productId in MspInProductTrialId]: string } = {
  [MspInProductTrialId.EdrTrial]: 'https://www.youtube-nocookie.com/embed/4CH_H-N8Uvk',
  [MspInProductTrialId.MdrTrial]: 'https://www.youtube-nocookie.com/embed/q7WUxXM0HRc'
};

export const GzLandingPageReportLinks: { [productId in MspInProductTrialId]: string } = {
  [MspInProductTrialId.MdrTrial]: 'https://mdr.bitdefender.com/en/reports/browse',
  [MspInProductTrialId.EdrTrial]: ''
}

export const COOLDOWN_DAYS = 180;

export enum MspInProductTrialActionButtonId {
  Reconfigure = 'ReconfigureButtonId',
  CreateReport = 'CreateReportButtonId',
}

export interface TrialCard {
  title: string;
  description: string;
  imageSrc: string;
  buttonId: string;
  buttonText: string;
}

export function getTrialCards() {
  return new Map<MspInProductTrialId, TrialCard>([
    [
      MspInProductTrialId.MdrTrial,
      {
        title: translateFn('Managed Detection and Response'),
        description: translateFn(
          "Empower a skilled team of experts to monitor security for your customers' business 24/7, identify threats, prevent attacks, and provide customized response actions and recommendations. Enhance overall security outcomes by getting access to advanced tools and efficient triage processes, and assist your customers in effectively addressing their security vulnerabilities."
        ),
        imageSrc: 'app/webcomponents/assets/images/trial/msp/productsHome/mdrHome.jpeg',
        buttonId: 'mdrTrial',
        buttonText: translateFn('Learn more'),
      },
    ],
    [
      MspInProductTrialId.EdrTrial,
      {
        title: translateFn('Endpoint Detection and Response'),
        description: translateFn(
          'Make use of extended threat detection, conduct focused investigations, and execute effective response strategies to effectively defend against potential cyber-attacks. EDR performs event correlation and is designed to identify advanced threats or attacks that are currently in progress, and assist incident response teams in their efforts to investigate and respond to advanced threats.'
        ),
        imageSrc: 'app/webcomponents/assets/images/trial/msp/productsHome/edrHome.jpeg',
        buttonId: 'edrTrial',
        buttonText: translateFn('Learn more'),
      },
    ],
  ]);
}

export function getMspIptGenericTexts() {
  return {
    reconfigureSuccessToastText: translateFn('Request received. If necessary, a Reconfigure agent task creation will be initiated.'),
    reconfigureErrorToastText: translateFn('Failed to create the Reconfigure agent task. Please try again later. If the problem persists, contact Support.'),
    reconfigureInfoToastText: translateFn('Request failed. Another Reconfigure agent task is currently being created.'),
  };
}

export function getReconfigureButtonItemTextsWhenActive():
  { [productId in MspInProductTrialId]: ButtonWithActionItem } {
  return {
    [MspInProductTrialId.EdrTrial]: {
      buttonItem: {
        buttonTitle: translateFn('EDR is ready to be deployed'),
        buttonDescription: translateFn('You can now deploy EDR on all of the company\'s endpoints.'),
        buttonText: translateFn('Add new module'),
        buttonId: MspInProductTrialActionButtonId.Reconfigure
      },
      confirmationWindowItem: {
        titleText: translateFn('Add EDR to the company\'s endpoints?'),
        bodyText: translateFn('You are about to deploy the EDR Sensor module on the company\'s endpoints using a Reconfigure agent task.'),
        confirmButtonText: translateFn('Continue'),
        cancelButtonText: translateFn('Cancel'),
        toastSuccessText: getMspIptGenericTexts().reconfigureSuccessToastText,
        toastErrorText: getMspIptGenericTexts().reconfigureErrorToastText,
        toastInfoText: getMspIptGenericTexts().reconfigureInfoToastText,
        isAddModulesAction: true
      },
    },
    [MspInProductTrialId.MdrTrial]: {
      buttonItem: {
        buttonTitle: translateFn('MDR is ready for setup'),
        buttonDescription: translateFn('You can now add the required modules to the company\'s endpoints to finish setting up MDR.'),
        buttonText: translateFn('Add new module'),
        buttonId: MspInProductTrialActionButtonId.Reconfigure
      },
      confirmationWindowItem: {
        titleText: translateFn('Add EDR to the company\'s endpoints?'),
        bodyText: translateFn('You are about to deploy the EDR Sensor module on the company\'s endpoints using a Reconfigure agent task.'),
        confirmButtonText: translateFn('Continue'),
        cancelButtonText: translateFn('Cancel'),
        toastSuccessText: getMspIptGenericTexts().reconfigureSuccessToastText,
        toastErrorText: getMspIptGenericTexts().reconfigureErrorToastText,
        toastInfoText: getMspIptGenericTexts().reconfigureInfoToastText,
        isAddModulesAction: true
      }
    }
  }
}

export function getReconfigureButtonItemTextsWhenInCooldown():
  { [productId in MspInProductTrialId]: ButtonWithActionItem } {
  return {
    [MspInProductTrialId.EdrTrial]: {
      buttonItem: {
        buttonTitle: translateFn('EDR is ready to be removed'),
        buttonDescription: translateFn('The EDR trial has ended and the installed modules are disabled. Use a Reconfigure agent task to remove them from the company\'s endpoints.'),
        buttonText: translateFn('Remove modules'),
        buttonId: MspInProductTrialActionButtonId.Reconfigure
      },
      confirmationWindowItem: {
        titleText: translateFn('Remove EDR from the company\'s endpoints?'),
        bodyText: translateFn('You are about to remove the EDR Sensor module from all the company\'s endpoints using a Reconfigure agent task.') +
                  translateFn('If you need additional information or want to initiate another trial for your managed company, please contact our Enterprise Support team.'),
        confirmButtonText: translateFn('Remove module'),
        cancelButtonText: translateFn('Dismiss'),
        toastSuccessText: getMspIptGenericTexts().reconfigureSuccessToastText,
        toastErrorText: getMspIptGenericTexts().reconfigureErrorToastText,
        toastInfoText: getMspIptGenericTexts().reconfigureInfoToastText,
        isAddModulesAction: false
      }
    },
    [MspInProductTrialId.MdrTrial]: {
      buttonItem: {
        buttonTitle: translateFn('The MDR associated modules are ready to be removed'),
        buttonDescription: translateFn('The MDR trial has ended and the installed modules are disabled. Use a Reconfigure agent task to remove them from the company\'s endpoints.'),
        buttonText: translateFn('Remove modules'),
        buttonId: MspInProductTrialActionButtonId.Reconfigure
      },
      confirmationWindowItem: {
        titleText: translateFn('Remove EDR from the company\'s endpoints?'),
        bodyText: translateFn('You are about to remove the EDR Sensor module from all the company\'s endpoints using a Reconfigure agent task.') +
                  translateFn('If you need additional information or want to initiate another trial for your managed company, please contact our Enterprise Support team.'),
        confirmButtonText: translateFn('Remove module'),
        cancelButtonText: translateFn('Dismiss'),
        toastSuccessText: getMspIptGenericTexts().reconfigureSuccessToastText,
        toastErrorText: getMspIptGenericTexts().reconfigureErrorToastText,
        toastInfoText: getMspIptGenericTexts().reconfigureInfoToastText,
        isAddModulesAction: false
      }
    }
  }
}

export function getGenerateReportButtonItem():
  { [productId in MspInProductTrialId]: ButtonItem } {
  return {
    [MspInProductTrialId.EdrTrial]: {
      buttonTitle: translateFn('Generate report'),
      buttonDescription: translateFn('The report provides a comprehensive overview of the trial period, resources monitored, and incidents detected by Bitdefender.'),
      buttonText: translateFn('Export report'),
      buttonId: MspInProductTrialActionButtonId.CreateReport
    },
    [MspInProductTrialId.MdrTrial]: {
      buttonTitle: translateFn('Generate report'),
      buttonDescription: translateFn('Access and analyze comprehensive data and gather important insights on the activity of the MDR feature on your customer companies.'),
      buttonText: translateFn('Export report'),
      buttonId: MspInProductTrialActionButtonId.CreateReport
    }
  }
}

export function translateProductIdReference(productId: MspInProductTrialId): string {
  switch (productId) {
    case MspInProductTrialId.EdrTrial:
      return 'msp edr ipt landing page';
    case MspInProductTrialId.MdrTrial:
      return 'msp mdr ipt landing page';
    default:
      return 'N/A';
  }
}

export interface MspIptButtonItems {
  reconfigureTaskButton?: ButtonItem;
  generateReportButton?: ButtonItem;
}

export const AvailableFeaturesForTrial = ['edr', 'mdr'];
